/**
 * BillingAccountSwitcher
 *
 * Header control for choosing the CHARGING account (subscription axis). The
 * credit-balance pill's contents live in the dropdown TRIGGER: the active
 * account's available balance, compact-formatted, with an hourglass reserved
 * chip when credits are held for in-flight actions (markup lifted from
 * CreditBalancePill). Opening the dropdown reveals one row per billable
 * account showing its label, plan slug, and the full available / reserved /
 * total triple. The checked radio is the active (isDefault) account; selecting
 * a different row sets it as active (charges route to it) via the set-active
 * mutation.
 *
 * All amounts stay live through the existing credit WebSocket events: every
 * credit activity event invalidates the ["billable-accounts"] query (see
 * useUnifiedEventConsumer), so each visible row refetches, with a 60s backstop.
 *
 * Loading → Skeleton. Error / empty → renders null so the header never breaks.
 * The single-account case still renders the trigger (the dropdown just shows
 * the one row).
 */
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Hourglass, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { CoinIcon } from "@/components/icons/CoinIcon";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useBillableAccounts,
  useSetActiveBillingAccount,
} from "@/hooks/useBillableAccounts";
import type { BillableAccount } from "@/services/billingApi";
import {
  formatCreditsCompact,
  formatCreditsExact,
} from "@/utils/formatCredits";

/** A single available/reserved/total figure line inside an account row. */
function AmountRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between gap-6">
      <span className="text-white/60">{label}</span>
      <span className="inline-flex items-center gap-0.5 tabular-nums">
        <CoinIcon
          className="h-5 w-5 text-muted-foreground"
          aria-hidden="true"
        />
        {formatCreditsExact(value)}
      </span>
    </div>
  );
}

export function BillingAccountSwitcher() {
  const { t } = useTranslation();
  const { accounts, activeAccount, isLoading, error } = useBillableAccounts();
  const setActive = useSetActiveBillingAccount();

  const handleValueChange = useCallback(
    (subscriptionId: string) => {
      if (subscriptionId === activeAccount?.subscriptionId) return;
      setActive.mutate(subscriptionId);
    },
    [activeAccount?.subscriptionId, setActive],
  );

  if (isLoading) {
    return (
      <Skeleton
        className="h-6 w-16 rounded-full"
        data-testid="billing-account-skeleton"
      />
    );
  }

  // Never break the header on a fetch error / empty list — render nothing.
  if (error || !activeAccount || accounts.length === 0) {
    return null;
  }

  const activeReserved = activeAccount.balance.reserved;
  const hasReserved = activeReserved > 0;
  const activeFrozen = activeAccount.balance.frozen ?? 0;
  const hasFrozen = activeFrozen > 0;
  // available is SIGNED — a reversal can drive the wallet into debt (< 0).
  const isOwed = activeAccount.balance.available < 0;
  const availableCompact = isOwed
    ? `-${formatCreditsCompact(Math.abs(activeAccount.balance.available))}`
    : formatCreditsCompact(activeAccount.balance.available);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5"
          data-testid="billing-account-trigger"
          aria-label={`${activeAccount.label}: ${
            isOwed
              ? t("credits.pill.owedAria", {
                  count: Math.abs(activeAccount.balance.available),
                })
              : t("credits.pill.available", {
                  count: activeAccount.balance.available,
                })
          }`}
        >
          {hasFrozen && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 px-1.5 py-0.5 text-xs text-amber-300"
              data-testid="billing-account-frozen"
              aria-label={t("credits.pill.frozenAria", {
                count: activeFrozen,
              })}
            >
              <Lock className="h-3 w-3" aria-hidden="true" />
              {formatCreditsExact(activeFrozen)}
            </span>
          )}

          {hasReserved && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-border px-1.5 py-0.5 text-xs text-muted-foreground"
              data-testid="billing-account-pending"
              aria-label={t("credits.pill.reservedAria", {
                count: activeReserved,
              })}
            >
              <Hourglass className="h-3 w-3" aria-hidden="true" />
              {formatCreditsExact(activeReserved)}
            </span>
          )}

          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm",
              isOwed
                ? "bg-destructive/15 text-destructive"
                : "bg-muted text-foreground",
            )}
          >
            <span className="font-normal" data-testid="billing-account-name">
              {activeAccount.label}
            </span>
            {/* Coin + amount are a tight unit, like a currency symbol. */}
            <span className="inline-flex items-center gap-0.5">
              <CoinIcon
                className="h-5 w-5 text-muted-foreground"
                aria-hidden="true"
                data-testid="billing-account-coin"
              />
              <span
                className="font-bold tabular-nums"
                data-testid="billing-account-amount"
                data-owed={isOwed || undefined}
              >
                {availableCompact}
              </span>
            </span>
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-[16rem]">
        <DropdownMenuLabel>{t("credits.switcher.title")}</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuRadioGroup
          value={activeAccount.subscriptionId}
          onValueChange={handleValueChange}
        >
          {accounts.map((account: BillableAccount) => (
            <DropdownMenuRadioItem
              key={account.subscriptionId}
              value={account.subscriptionId}
              disabled={setActive.isPending}
              className="flex-col items-stretch gap-1"
              data-testid={`billing-account-row-${account.subscriptionId}`}
            >
              <div className="flex flex-col">
                <span className="font-medium text-white">{account.label}</span>
                <span className="text-xs text-white/50">
                  {account.planSlug}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 text-xs">
                <AmountRow
                  label={t("credits.popup.available")}
                  value={account.balance.available}
                />
                {account.balance.reserved > 0 && (
                  <AmountRow
                    label={t("credits.popup.reserved")}
                    value={account.balance.reserved}
                  />
                )}
                {(account.balance.frozen ?? 0) > 0 && (
                  <AmountRow
                    label={t("credits.popup.frozen")}
                    value={account.balance.frozen}
                  />
                )}
                <AmountRow
                  label={t("credits.popup.total")}
                  value={account.balance.total}
                />
              </div>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
